import React from 'react';
import { Clock, Truck, Percent, Info, Minus, Plus, Undo2, AlertTriangle, FileText } from 'lucide-react';
import { Skeleton } from '../../ui/skeleton';
import { eur, hoursLabel } from './devisFormat';

const ADJUST_STEP = 0.25;

/**
 * Résultat du calcul : temps par prestation (ajustable ± 15 min), évacuation, lignes de devis et totaux.
 * `onAdjust(blockId, deltaHours)` : ajustement manuel cumulé du bloc (tracé dans la note privée Dolibarr).
 */
export function DevisResult({ result, loading, error, adjustments = {}, onAdjust }) {
  if (error) {
    return <p className="text-sm text-destructive" role="alert" data-testid="devis-result-error">{error}</p>;
  }
  if (!result) {
    return loading ? <Skeleton className="h-24 w-full" /> : (
      <p className="text-sm text-muted-foreground flex items-start gap-2" data-testid="devis-result-empty"><Info className="h-4 w-4 mt-0.5 flex-shrink-0" /> Renseignez une haie, une pelouse ou une autre prestation : le temps et le prix se calculent au fur et à mesure.</p>
    );
  }
  const t = result.totals;
  return (
    <div className={`space-y-3 ${loading ? 'opacity-60' : ''}`} data-testid="devis-result">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-secondary/60 p-2.5">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> Temps facturé</p>
          <p className="text-lg font-bold" data-testid="devis-hours-total">{result.hours.total_label}</p>
          {result.hours.min_applied && <p className="text-[11px] text-muted-foreground">minimum appliqué</p>}
        </div>
        <div className="rounded-lg bg-secondary/60 p-2.5">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Truck className="h-3 w-3" /> Évacuation</p>
          <p className="text-lg font-bold" data-testid="devis-trailers">{result.evacuation.trailers} remorque{result.evacuation.trailers > 1 ? 's' : ''}</p>
          {result.evacuation.volume_m3 > 0 && <p className="text-[11px] text-muted-foreground">≈ {String(result.evacuation.volume_m3).replace('.', ',')} m³</p>}
        </div>
      </div>

      {result.blocks.length > 0 && (
        <ul className="space-y-1.5" data-testid="devis-blocks">
          {result.blocks.map((b) => {
            const adj = Number(adjustments[b.id]) || 0;
            return (
              <li key={b.id} className="text-sm border-b border-border/60 pb-1.5 last:border-0" data-testid={`devis-block-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="font-medium">{b.label}</span>
                    <span className="block text-xs text-muted-foreground">{b.text.replace(`${b.label} : `, '')}</span>
                    {b.trailers > 0 && <span className="block text-xs text-muted-foreground">{String(b.volume_m3).replace('.', ',')} m³ → {b.trailers} remorque{b.trailers > 1 ? 's' : ''}</span>}
                    {b.bags > 0 && <span className="block text-xs text-muted-foreground">{b.bags} sacs · mise en sac {hoursLabel(b.bag_hours)}</span>}
                  </span>
                  <span className="text-right whitespace-nowrap">
                    <span className="font-semibold" data-testid={`devis-block-${b.id}-hours`}>{b.hours_billed_label}</span>
                    {adj !== 0 && <span className="block text-[11px] text-[hsl(var(--brand-apricot))]" data-testid={`devis-block-${b.id}-adjusted`}>calcul {b.hours_label} · {adj > 0 ? '+' : ''}{String(adj).replace('.', ',')} h</span>}
                  </span>
                </div>
                {onAdjust && (
                  <div className="mt-1 flex items-center justify-end gap-1" aria-label={`Ajuster le temps de ${b.label}`}>
                    <span className="text-[11px] text-muted-foreground mr-1">Ajuster</span>
                    <button type="button" onClick={() => onAdjust(b.id, -ADJUST_STEP)} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-border hover:bg-secondary" aria-label="Moins 15 minutes" data-testid={`devis-block-${b.id}-minus`}><Minus className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => onAdjust(b.id, ADJUST_STEP)} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-border hover:bg-secondary" aria-label="Plus 15 minutes" data-testid={`devis-block-${b.id}-plus`}><Plus className="h-3.5 w-3.5" /></button>
                    {adj !== 0 && <button type="button" onClick={() => onAdjust(b.id, null)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-secondary" aria-label="Annuler l'ajustement" data-testid={`devis-block-${b.id}-reset`}><Undo2 className="h-3.5 w-3.5" /></button>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm" data-testid="devis-lines">
          <thead className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr><th className="text-left px-2 py-1.5 font-medium">Ligne Dolibarr</th><th className="text-right px-2 py-1.5 font-medium">Qté</th><th className="text-right px-2 py-1.5 font-medium">TTC</th></tr>
          </thead>
          <tbody>
            {result.lines.map((l, i) => (
              <tr key={i} className="border-t border-border/60" data-testid={`devis-line-${l.kind}-${i}`}>
                <td className="px-2 py-1.5"><span className="font-medium">{l.ref}</span> <span className="text-muted-foreground text-xs">{l.desc.split('\n')[0]}</span>{l.sap_eligible === false && <span className="ml-1 inline-block rounded px-1 py-px text-[10px] font-medium bg-[hsl(var(--brand-apricot))]/25 align-middle" data-testid={`devis-line-${i}-non-sap`}>hors crédit d'impôt</span>}</td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap">{String(l.qty).replace('.', ',')}{l.kind === 'hours' ? ' h' : ''}</td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap">{eur(l.total_ttc)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl bg-[hsl(var(--primary))] text-white p-3">
        <div className="flex items-baseline justify-between">
          <span className="text-sm opacity-90">Total {result.scope === 'contrat' ? `contrat (${result.visits} passages)` : 'par passage'}</span>
          <span className="text-2xl font-bold" data-testid="devis-total-ttc">{eur(t.ttc)}</span>
        </div>
        <p className="text-xs opacity-80">{eur(t.ht)} HT · TVA {eur(t.tva)} · tarif horaire {eur(result.hourly.price_ttc)} TTC ({result.hourly.ref}){result.discount_percent > 0 ? ` · remise ${String(result.discount_percent).replace('.', ',')} % incluse` : ''}</p>
        <div className="mt-2 rounded-lg bg-white/15 px-2.5 py-1.5 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm flex items-center gap-1"><Percent className="h-3.5 w-3.5" /> Reste à charge après avance immédiate</span>
            <span className="font-semibold" data-testid="devis-after-credit">{eur(t.after_tax_credit)}</span>
          </div>
          <p className="text-[11px] opacity-85" data-testid="devis-credit-detail">
            Crédit d'impôt {eur(t.tax_credit)} ({Math.round((t.tax_credit_rate ?? 0.5) * 100)} % de {eur(t.eligible_ttc ?? t.ttc)} éligibles{t.tax_credit_capped ? `, plafonné à ${eur(t.tax_credit_cap)}` : ''})
          </p>
          {t.non_eligible_ttc > 0 && (
            <p className="text-[11px] flex items-start gap-1 rounded bg-white/20 px-1.5 py-1" data-testid="devis-non-eligible">
              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>{eur(t.non_eligible_ttc)} TTC hors services à la personne (plein tarif, sans crédit d'impôt) : {(t.non_eligible_lines || []).join(', ')}</span>
            </p>
          )}
        </div>
        {t.contract && result.scope !== 'contrat' && (
          <p className="text-xs opacity-90 mt-2" data-testid="devis-contract-total">Contrat {t.contract.visits} passages : {eur(t.contract.ttc)} TTC, soit {eur(t.contract.after_tax_credit)} après avance immédiate{t.contract.capped ? ' (plafond appliqué)' : ''}.</p>
        )}
      </div>
      {result.public_note && (
        <details className="rounded-lg border border-border bg-secondary/40 text-xs" data-testid="devis-public-note">
          <summary className="cursor-pointer select-none px-3 py-2 font-medium flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Mentions imprimées sur le devis</summary>
          <p className="px-3 pb-3 whitespace-pre-line text-muted-foreground" data-testid="devis-public-note-text">{result.public_note}</p>
        </details>
      )}
      {result.warnings?.map((w) => <p key={w} className="text-xs text-muted-foreground flex items-start gap-1" data-testid="devis-warning"><Info className="h-3 w-3 mt-0.5 flex-shrink-0" />{w}</p>)}
    </div>
  );
}
